import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface HomeVisitCity {
  id: string;
  city: string;
  postal_code: string | null;
  sector: string | null;
  surcharge_amount: number;
  is_active: boolean;
  sort_order: number;
}

export const normalizeCity = (value: string) =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

export interface ZoneCheckResult {
  inZone: boolean;
  city: HomeVisitCity | null;
  surcharge: number;
}

export function checkHomeVisitZone(
  cities: HomeVisitCity[],
  cityName: string,
  postalCode?: string,
): ZoneCheckResult {
  const target = normalizeCity(cityName || "");
  if (!target) return { inZone: false, city: null, surcharge: 0 };

  const matches = cities.filter((c) => c.is_active && normalizeCity(c.city) === target);
  const match =
    matches.find((c) => !postalCode || !c.postal_code || c.postal_code === postalCode.trim()) || matches[0] || null;

  return {
    inZone: !!match,
    city: match,
    surcharge: match ? Number(match.surcharge_amount || 0) : 0,
  };
}

export function useHomeVisitCities(activeOnly = true) {
  const [cities, setCities] = useState<HomeVisitCity[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    let query = supabase
      .from("home_visit_cities")
      .select("id, city, postal_code, sector, surcharge_amount, is_active, sort_order")
      .order("sort_order", { ascending: true })
      .order("city", { ascending: true });

    if (activeOnly) query = query.eq("is_active", true);

    const { data, error } = await query;
    if (error) console.error("Erreur chargement des villes à domicile:", error);
    setCities((data as HomeVisitCity[]) || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOnly]);

  return { cities, loading, reload: load };
}
